import { useState } from "react";
import { ArrowUpRight, ArrowDown, MoveRight } from "lucide-react";
import { Line, Reveal, Overline } from "@/components/Kinetic";
import FlowDiagram from "@/components/FlowDiagram";
import { CERNIO_STEPS_EN, CERNIO_STEPS_TR } from "@/components/home/CernioFeature";
import { useLang } from "@/i18n/LanguageContext";

const FLOW_STEPS_EN = [
  { index: "01", title: "Product", desc: "Category & use case", chips: ["Category", "Use case", "Price position"] },
  { index: "02", title: "Channel", desc: "Who should sell it", chips: ["Distributor profile", "Buyer profile"] },
  { index: "03", title: "Market", desc: "Where to look", chips: ["Regions", "Demand signals", "Structure"] },
  { index: "04", title: "Companies", desc: "Who may be relevant", chips: ["Relevance match", "Discovery"] },
  { index: "05", title: "Opportunities", desc: "What to pursue", chips: ["Structured review"] },
];

const FLOW_STEPS_TR = [
  { index: "01", title: "Ürün", desc: "Kategori ve kullanım alanı", chips: ["Kategori", "Kullanım alanı", "Fiyat konumu"] },
  { index: "02", title: "Kanal", desc: "Onu kim satmalı", chips: ["Distribütör profili", "Alıcı profili"] },
  { index: "03", title: "Pazar", desc: "Nereye bakmalı", chips: ["Bölgeler", "Talep sinyalleri", "Pazar yapısı"] },
  { index: "04", title: "Şirketler", desc: "Kimler uygun olabilir", chips: ["Uygunluk eşleşmesi", "Keşif"] },
  { index: "05", title: "Fırsatlar", desc: "Neyi takip etmeli", chips: ["Yapılandırılmış inceleme"] },
];

const PARTNER_TYPES_EN = [
  { key: "distributors", label: "Distributors", profile: "Companies with regional logistics, existing retail or trade networks, and complementary product lines." },
  { key: "importers", label: "Importers", profile: "Firms already importing related product categories into your target market." },
  { key: "dealers", label: "Dealers", profile: "Local sellers with established customer relationships in your product's segment." },
  { key: "wholesalers", label: "Wholesalers", profile: "Volume buyers supplying fragmented retail or professional channels." },
  { key: "buyers", label: "Buyers", profile: "Direct commercial buyers whose operations match what your product is built for." },
  { key: "channel-partners", label: "Channel partners", profile: "Agents, representatives and integrators who can carry a product into a market." },
];

const PARTNER_TYPES_TR = [
  { key: "distributors", label: "Distribütörler", profile: "Bölgesel lojistiğe, mevcut perakende veya ticaret ağlarına ve tamamlayıcı ürün gruplarına sahip şirketler." },
  { key: "importers", label: "İthalatçılar", profile: "Hedef pazarınıza ilgili ürün kategorilerinde ithalat yapan firmalar." },
  { key: "dealers", label: "Bayiler", profile: "Ürününüzün segmentinde yerleşik müşteri ilişkileri olan yerel satıcılar." },
  { key: "wholesalers", label: "Toptancılar", profile: "Parçalı perakende veya profesyonel kanallara tedarik sağlayan hacimli alıcılar." },
  { key: "buyers", label: "Alıcılar", profile: "Operasyonları ürününüzün amacına uyan doğrudan ticari alıcılar." },
  { key: "channel-partners", label: "Kanal ortakları", profile: "Bir ürünü pazara taşıyabilen acenteler, temsilciler ve entegratörler." },
];

const USE_CASES_EN = [
  { index: "01", title: "Industrial machinery", text: "Find distributors and dealers who already serve the sectors your equipment is built for." },
  { index: "02", title: "Food & agriculture", text: "Identify importers and wholesalers active in your product category and destination markets." },
  { index: "03", title: "Textiles & apparel", text: "Map buyers and channel partners across fragmented regional markets." },
  { index: "04", title: "Chemicals & materials", text: "Reach the specialist distributors that general company searches rarely surface." },
  { index: "05", title: "Technology products", text: "Discover resellers and integration partners positioned for your solution." },
];

const USE_CASES_TR = [
  { index: "01", title: "Endüstriyel makine", text: "Ekipmanınızın hedeflediği sektörlere hâlihazırda hizmet veren distribütörleri ve bayileri bulun." },
  { index: "02", title: "Gıda ve tarım", text: "Ürün kategorinizde ve hedef pazarlarınızda aktif ithalatçıları ve toptancıları belirleyin." },
  { index: "03", title: "Tekstil ve hazır giyim", text: "Parçalı bölgesel pazarlarda alıcıları ve kanal ortaklarını haritalayın." },
  { index: "04", title: "Kimyasallar ve malzemeler", text: "Genel şirket aramalarının nadiren ortaya çıkardığı uzman distribütörlere ulaşın." },
  { index: "05", title: "Teknoloji ürünleri", text: "Çözümünüze uygun satıcıları ve entegrasyon ortaklarını keşfedin." },
];

export default function CernioPage() {
  const { lang, tx } = useLang();
  const [activeKey, setActiveKey] = useState("distributors");
  const flowSteps = lang === "tr" ? FLOW_STEPS_TR : FLOW_STEPS_EN;
  const howSteps = lang === "tr" ? CERNIO_STEPS_TR : CERNIO_STEPS_EN;
  const partnerTypes = lang === "tr" ? PARTNER_TYPES_TR : PARTNER_TYPES_EN;
  const useCases = lang === "tr" ? USE_CASES_TR : USE_CASES_EN;
  const active = partnerTypes.find((p) => p.key === activeKey) || partnerTypes[0];


  return (
    <>
      <section className="bg-ink pt-32 text-slate-300 sm:pt-40 lg:pt-44">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <Reveal immediate y={16}>
                <Overline dark>{tx("GADULABS — Flagship product", "GADULABS — Amiral gemisi ürün")}</Overline>
              </Reveal>
              <h1
                data-testid="cernio-hero-title"
                className="mt-7 font-display text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
              >
                {lang === "tr" ? (
                  <>
                    <Line delay={0.1}>Doğru şirketleri,</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">doğru pazarlarda bulun.</span>
                    </Line>
                  </>
                ) : (
                  <>
                    <Line delay={0.1}>Find the right companies</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">in the right markets.</span>
                    </Line>
                  </>
                )}
              </h1>
              <Reveal immediate delay={0.45} y={20} className="mt-7 max-w-xl">
                <p className="text-base leading-relaxed text-slate-400 sm:text-lg">
                  {tx(
                    "Cernio is an AI-powered market and channel intelligence platform for exporters. It understands what you sell, who should sell it, and where those companies can be found.",
                    "Cernio, ihracatçılar için yapay zekâ destekli bir pazar ve kanal istihbaratı platformudur. Ne sattığınızı, onu kimin satması gerektiğini ve bu şirketlerin nerede bulunabileceğini anlar.",
                  )}
                </p>
              </Reveal>
              <Reveal immediate delay={0.6} y={20} className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="https://getcernio.com"
                  data-testid="cernio-hero-cta"
                  className="focus-ring group inline-flex items-center gap-2 bg-cobalt px-6 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-ink"
                >
                  {tx("Visit Cernio", "Cernio’ya gidin")}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </a>
                <a
                  href="#how"
                  data-testid="cernio-hero-secondary"
                  className="focus-ring inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-white/60"
                >
                  {tx("How it works", "Nasıl çalışır")}
                  <ArrowDown size={15} />
                </a>
              </Reveal>
            </div>
            <Reveal immediate delay={0.5} y={32} className="lg:col-span-5">
              <FlowDiagram
                steps={flowSteps}
                dark
                label={tx("System / Product → Opportunity", "Sistem / Ürün → Fırsat")}
                testid="cernio-flow"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline>{tx("Positioning", "Konumlandırma")}</Overline>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <h2 className="font-display text-3xl font-bold leading-snug tracking-tight text-ink sm:text-4xl">
                  {tx("Cernio is not a company database.", "Cernio bir şirket veritabanı değildir.")}
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  {tx(
                    "Discovery starts from product and channel understanding — not from keyword searches over a static list. The companies Cernio surfaces are candidates for evaluation, not pre-qualified leads. Your team makes the final call, with better context.",
                    "Keşif, statik bir liste üzerinde anahtar kelime aramalarından değil, ürün ve kanal anlayışından başlar. Cernio'nun ortaya çıkardığı şirketler önceden nitelendirilmiş müşteri adayları değil, değerlendirilecek adaylardır. Nihai kararı, daha iyi bir bağlamla ekibiniz verir.",
                  )}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="bg-paper py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("How Cernio works", "Cernio nasıl çalışır")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              {tx("From product to market opportunity.", "Üründen pazar fırsatına.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
            {howSteps.map((s, i) => (
              <Reveal key={s.index} delay={i * 0.07} y={22} className="bg-paper">
                <div
                  data-testid={`cernio-how-step-${i + 1}`}
                  className="flex h-full flex-col p-7 transition-colors duration-300 hover:bg-white"
                >
                  <span className="font-mono text-xs tracking-[0.15em] text-gold">
                    {s.index}
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {s.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("Discovery targets", "Keşif hedefleri")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              {tx(
                "Discover who should sell your product — and where.",
                "Ürününüzü kimin satması gerektiğini — ve nerede — keşfedin.",
              )}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {partnerTypes.map((p) => {
                  const isActive = active.key === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      data-testid={`partner-type-${p.key}`}
                      onClick={() => setActiveKey(p.key)}
                      aria-pressed={isActive}
                      className={`focus-ring group flex items-center justify-between border p-5 text-left transition-colors duration-300 ${
                        isActive
                          ? "border-ink bg-ink text-white"
                          : "border-slate-200 bg-paper text-ink hover:border-slate-400"
                      }`}
                    >
                      <span className="font-display text-base font-bold tracking-tight">
                        {p.label}
                      </span>
                      <MoveRight
                        size={16}
                        className={`transition-all duration-300 ${
                          isActive ? "text-gold" : "text-slate-300 group-hover:text-slate-500"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div
                data-testid="partner-profile-panel"
                className="h-full border border-slate-200 bg-paper p-8"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                  {tx("Discovery profile — illustrative", "Keşif profili — örnek")}
                </span>
                <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-ink">
                  {active.label}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  {active.profile}
                </p>
                <p className="mt-8 border-t border-slate-200 pt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  {tx(
                    "Matched against product, channel & market context",
                    "Ürün, kanal ve pazar bağlamıyla eşleştirilir",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-paper py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("Use cases", "Kullanım alanları")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {tx("Built for exporters.", "İhracatçılar için.")}
            </h2>
          </Reveal>
          <div className="mt-12 border-t border-slate-200">
            {useCases.map((u, i) => (
              <Reveal key={u.index} delay={i * 0.06} y={18}>
                <div
                  data-testid={`use-case-${u.index}`}
                  className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-b border-slate-200 py-6 sm:grid-cols-[auto_auto_1fr] sm:gap-10"
                >
                  <span className="font-mono text-xs tracking-[0.15em] text-slate-400 transition-colors duration-300 group-hover:text-gold">
                    {u.index}
                  </span>
                  <h3 className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">
                    {u.title}
                  </h3>
                  <p className="col-span-2 max-w-xl text-sm leading-relaxed text-slate-600 sm:col-span-1 sm:justify-self-end sm:text-right">
                    {u.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 sm:py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline dark>{tx("Get started", "Başlayın")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
              {tx("Start from your product.", "Ürününüzden başlayın.")}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              {tx(
                "Explore Cernio’s product website to see how product context becomes a company shortlist with relevance reasoning and a place to continue your research.",
                "Ürün bilgisinin uygunluk gerekçeleriyle birlikte bir aday şirket listesine nasıl dönüştüğünü ve araştırmanın nasıl sürdürüldüğünü Cernio’nun kendi sitesinde inceleyin.",
              )}
            </p>
          </Reveal>
          <Reveal delay={0.28} className="mt-10">
            <a
              href="https://getcernio.com"
              data-testid="cernio-final-cta"
              className="focus-ring group inline-flex items-center gap-2 bg-cobalt px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-ink"
            >
              {tx("Visit Cernio", "Cernio’ya gidin")}
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
